import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import {
    Popover,
    PopoverButton,
    PopoverPanel,
    Transition,
} from '@headlessui/react';

import { fetchWidgetChannels } from 'flow/actions/channels-actions';
import { fetchQueues } from 'flow/actions/pbx-actions';
import { fetchProfiles } from 'flow/actions/profile-actions';
import useAppContext from 'flow/hooks/useAppContext';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { getActiveUntil } from 'flow/utils';
import { isTeamsTypeIntegration } from 'flow/utils/app-utils';
import { getStandardProfileDescriptionInUserLanguage } from 'flow/utils/profile-utils';
import { twMerge } from 'tailwind-merge';
import ProfilePickerMenu from '../ProfilePicker/Menu';
import { ProfileToFocus } from '../ProfilePicker/Profiles';
import Icon from './Icon';
import {
    selectExtensionActiveProfile,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';

interface Props {
    extensionKey: ExtensionKey;
    showSettingsLink?: boolean;
    enableLogInOptions?: boolean;
    fixedWidth?: boolean;
}

const ProfilePicker = ({
    extensionKey = null,
    showSettingsLink,
    enableLogInOptions,
    fixedWidth = false,
}: Props) => {
    const { embedded, i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    useEffect(() => {
        dispatch(fetchProfiles(extensionKey));
    }, [dispatch, extensionKey]);

    useEffect(() => {
        dispatch(fetchQueues());
        dispatch(fetchWidgetChannels('apps'));
    }, [dispatch]);

    const activeProfile = useAppSelector((state) =>
        selectExtensionActiveProfile(state, extensionKey || ownExtensionKey),
    );

    // ArrowDown and ArrowUp on the button open the picker on the first or last
    // profile, as a menu button does. Enter and Space open it with focus left
    // on the button, so Tab reaches the status message field first.
    const [profileToFocus, setProfileToFocus] = useState<ProfileToFocus>(null);
    const clearProfileToFocus = useCallback(() => setProfileToFocus(null), []);

    const handleButtonKeyDown = (
        event: React.KeyboardEvent<HTMLButtonElement>,
        open: boolean,
    ) => {
        if (event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        const target: ProfileToFocus =
            { ArrowDown: 'first' as const, ArrowUp: 'last' as const }[
                event.key
            ] ?? null;

        if (!target) {
            return;
        }

        event.preventDefault();
        setProfileToFocus(target);

        if (!open) {
            event.currentTarget.click();
        }
    };

    // A Popover, not a Menu: the panel holds a text field, tabs and switches,
    // which a menu cannot contain, and a menu closes on Tab. A popover lets Tab
    // walk through the panel and closes once focus leaves it.
    return (
        <Popover className="w-full self-start">
            {({ open }) => (
                <div
                    className={twMerge(
                        'relative',
                        fixedWidth ? 'max-w-full @[360px]:w-[320px]' : 'w-full',
                    )}
                >
                    <div
                        className={twMerge(
                            'rounded-sm md:inline-flex',
                            !fixedWidth ? 'w-full' : '',
                        )}
                    >
                        <PopoverButton
                            id="profile-picker-button"
                            onKeyDown={(event) =>
                                handleButtonKeyDown(event, open)
                            }
                            className={twMerge(
                                'focus-ring inline-flex w-full items-center justify-between rounded-sm bg-gray-200 hover:bg-gray-300',
                                fixedWidth ? '@[360px]:max-w-[280px]' : '',
                            )}
                        >
                            <div className="inline-flex flex-wrap items-center gap-x-1 ps-3 pe-1 text-sm/4 text-gray-800">
                                {!activeProfile ? (
                                    i18next.t('status.unavailable')
                                ) : (
                                    <>
                                        <p className="max-w-[184px] truncate font-medium">
                                            {getStandardProfileDescriptionInUserLanguage(
                                                activeProfile.get(
                                                    'description',
                                                ),
                                                i18next,
                                            )}
                                        </p>
                                        <p className="text-gray-500">
                                            {getActiveUntil(
                                                activeProfile,
                                                i18next,
                                            )}
                                        </p>
                                    </>
                                )}
                            </div>
                            <span className="sr-only">
                                {i18next.t('Tooltip.Nav.ProfilePicker')}
                            </span>
                            <div className="ms-auto h-[40px] p-2 md:ms-0">
                                <Icon iconKey="arrow_down" />
                            </div>
                        </PopoverButton>
                    </div>
                    <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className={twMerge(
                                'shadow-base absolute inset-x-0 z-30 rounded-sm bg-white text-gray-800 @[360px]:inset-auto @[360px]:end-0 dark:bg-gray-200',
                                !fixedWidth ? '@[360px]:w-[320px]' : 'w-full',
                                isTeamsTypeIntegration(embedded)
                                    ? '-top-2 mb-1 -translate-y-full transform'
                                    : 'mt-1',
                            )}
                        >
                            <PopoverPanel className="w-full origin-top-right overflow-hidden py-3 md:max-h-[520px] rtl:origin-top-left">
                                <ProfilePickerMenu
                                    enableLogInOptions={enableLogInOptions}
                                    showSettingsLink={showSettingsLink}
                                    extensionKey={extensionKey}
                                    profileToFocus={profileToFocus}
                                    onProfileToFocusHandled={
                                        clearProfileToFocus
                                    }
                                />
                            </PopoverPanel>
                        </div>
                    </Transition>
                </div>
            )}
        </Popover>
    );
};

export default ProfilePicker;
